import { computed } from 'vue';
import { injectLocal, provideLocal } from '@vueuse/core';
import { useBrand } from '@composables/useBrand';
import { useProductType } from '@composables/useProductType';
import { useProduct } from '@modules/product';
import {
  useProductFlags,
  useFeatureEligibility,
  useTopLevelCategoryFlags,
} from '@modules/featureControl';
import { useProductDetails } from './useProductDetails';
import { PRODUCT_DETAILS_AVAILABILITY_REPOSITORY } from '../constants';

export const useProductDetailsAvailability = () => injectLocal(PRODUCT_DETAILS_AVAILABILITY_REPOSITORY);

export const useProductDetailsAvailabilityProvide = () => {
  const { productData } = useProduct();
  const { isBcom, isMcom } = useBrand();
  const { isUnavailable } = useProductType();
  const {
    isSizeAndFitEnabledFlag,
    gwpExperienceEnabledFlag,
    isMemberOfMasterFlag,
    homeFOBFlag,
    giftCardFlag,
  } = useProductFlags();
  const {
    isBeautyIngredientsVisible,
    marketPlaceItemEnabled,
    isProductDimensionsVisible,
    isBigTicketV2C,
    isBigTicket,
    isSalesFloorEnabled,
  } = useFeatureEligibility();
  const {
    dimensionsTitle,
    productSummaryBullets,
    sizeAndFit,
    dimensionsBullets,
    materialsAndCareBullets,
  } = useProductDetails();
  const { isHomeCategory } = useTopLevelCategoryFlags();

  const isSummaryAvailable = computed(() => !!productSummaryBullets.value.length);
  const isSizeAndFitAvailable = isBcom ? (isSizeAndFitEnabledFlag && !!sizeAndFit.length) : !!sizeAndFit.length;
  const isIngredientsAvailable = isBcom && !!dimensionsBullets.length
    && !!dimensionsTitle
    && !isUnavailable
    && isBeautyIngredientsVisible;

  const isMktDimensionsAvailable = isBcom && marketPlaceItemEnabled && !isSizeAndFitAvailable;
  const isDimensionsEnabled = !isUnavailable && isProductDimensionsVisible;
  const isDimensionsAvailable = !!dimensionsBullets.length && (isDimensionsEnabled || isMktDimensionsAvailable);
  const isMaterialAndCareAvailable = !!materialsAndCareBullets.length;
  const isInstructionsAvailable = isMcom && gwpExperienceEnabledFlag;
  const isTermsAndConditionsAvailable = isMcom && gwpExperienceEnabledFlag;
  const isWhiteGloveDeliveryAvailable = isBcom && !isBigTicketV2C && isBigTicket && !isMemberOfMasterFlag;
  const isShippingAndReturnsAvailable = !!Object.keys(productData?.shipping || {}).length && !isWhiteGloveDeliveryAvailable;

  // TODO: validateHomeProduct temp solution, we have same check within SalesFloorWidget component and have to do it in common place
  const validateHomeProduct = (isMcom && homeFOBFlag) ? isHomeCategory : true;
  const isSalesFloorWidgetAvailable = isSalesFloorEnabled && validateHomeProduct && !giftCardFlag;

  provideLocal(PRODUCT_DETAILS_AVAILABILITY_REPOSITORY, {
    isSummaryAvailable,
    isSizeAndFitAvailable,
    isIngredientsAvailable,
    isDimensionsAvailable,
    isMaterialAndCareAvailable,
    isInstructionsAvailable,
    isTermsAndConditionsAvailable,
    isWhiteGloveDeliveryAvailable,
    isShippingAndReturnsAvailable,
    isSalesFloorWidgetAvailable,
  });
};
